// Typed fetch wrapper for every route in docs/API_CONTRACT.md.
// Real requests are used when VITE_USE_MOCK=false; otherwise every call is served by
// api/mock.ts's in-memory implementation, so the app is fully demoable with no backend.

import { config } from '../config';
import { refreshIdToken } from '../auth/cognito';
import { restoreSession } from '../auth/cognitoSrp';
import { getIdToken, getRefreshToken, setIdToken } from '../auth/token';
import { mockApi } from './mock';
import type { Tradition } from '../theme/tokens';
import type {
  AdminAnalytics,
  AdminUser,
  AdminUsersResponse,
  CalendarResponse,
  CreateCheckoutResponse,
  CreateJournalEntryBody,
  JournalEntriesResponse,
  JournalEntry,
  LeaderboardResponse,
  LibraryDownload,
  LibraryResponse,
  Me,
  PillarsResponse,
  PracticeAudio,
  PracticesResponse,
  PracticeType,
  PostSessionBody,
  PostSessionResponse,
  ReinforcementResponse,
  Routine,
  ThirtyDaySchedule,
  Tier,
  TodayPlan,
  TodayQuotes,
  UpdateJournalEntryBody,
  UpdateMeBody,
  UpdateRoutineBody,
} from './types';

class ApiError extends Error {
  status: number;
  /** Present on a 403 from the content-access gate (see PracticeAccess) -- lets callers branch
   * on the precise reason instead of parsing the human-readable message text. */
  access?: string;
  constructor(status: number, message: string, access?: string) {
    super(message);
    this.status = status;
    this.access = access;
    this.name = 'ApiError';
  }
}

async function doFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
}

/** Tries every way this app might be able to silently mint a fresh ID token:
 * the SRP sign-in path caches its session inside amazon-cognito-identity-js
 * itself (restoreSession), while the Google/Hosted-UI path only ever gave us
 * a raw refresh_token to hang onto ourselves (auth/token.ts). Returns null if
 * neither works, meaning the user genuinely needs to sign in again. */
async function silentlyRefreshIdToken(): Promise<string | null> {
  const viaSrpSession = await restoreSession().catch(() => null);
  if (viaSrpSession) return viaSrpSession;

  const storedRefreshToken = getRefreshToken();
  if (storedRefreshToken) {
    const viaRefreshToken = await refreshIdToken(storedRefreshToken).catch(() => null);
    if (viaRefreshToken) return viaRefreshToken;
  }

  return null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await doFetch(path, init, getIdToken());

  // The Cognito ID token is only valid for ~1 hour; a long-lived tab hits this
  // constantly. Rather than silently failing every call (and every save)
  // once it expires, try a silent refresh and retry once before giving up.
  if (res.status === 401) {
    const refreshed = await silentlyRefreshIdToken();
    if (refreshed) {
      setIdToken(refreshed);
      res = await doFetch(path, init, refreshed);
    }
  }

  if (!res.ok) {
    let message = res.statusText;
    let access: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; access?: string };
      if (body?.error) message = body.error;
      access = body?.access;
    } catch {
      // response body wasn't JSON -- keep statusText
    }
    throw new ApiError(res.status, message, access);
  }

  return (await res.json()) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${search.toString()}`;
}

export const api = {
  async getMe(): Promise<Me> {
    if (config.useMock) return mockApi.getMe();
    return request<Me>('/api/me');
  },

  async updateMe(body: UpdateMeBody): Promise<Me> {
    if (config.useMock) return mockApi.updateMe(body);
    return request<Me>('/api/me', { method: 'PUT', body: JSON.stringify(body) });
  },

  async getTodayQuotes(): Promise<TodayQuotes> {
    if (config.useMock) return mockApi.getTodayQuotes();
    return request<TodayQuotes>('/api/quotes/today');
  },

  async getPractices(params: { type?: PracticeType; tradition?: Tradition } = {}): Promise<PracticesResponse> {
    if (config.useMock) return mockApi.getPractices(params);
    return request<PracticesResponse>(`/api/practices${qs(params)}`);
  },

  async getPillars(params: { tradition?: Tradition; all?: boolean } = {}): Promise<PillarsResponse> {
    if (config.useMock) return mockApi.getPillars(params);
    return request<PillarsResponse>(`/api/pillars${qs({ tradition: params.tradition, all: params.all ? 'true' : undefined })}`);
  },

  async getCalendar(params: { tradition?: Tradition } = {}): Promise<CalendarResponse> {
    if (config.useMock) return mockApi.getCalendar(params);
    return request<CalendarResponse>(`/api/calendar${qs(params)}`);
  },

  async getPracticeAudio(practiceId: string): Promise<PracticeAudio> {
    if (config.useMock) return mockApi.getPracticeAudio(practiceId);
    return request<PracticeAudio>(`/api/practices/${encodeURIComponent(practiceId)}/audio`);
  },

  async getLibrary(tradition?: Tradition): Promise<LibraryResponse> {
    if (config.useMock) return mockApi.getLibrary(tradition);
    return request<LibraryResponse>(`/api/library${qs({ tradition })}`);
  },

  async getLibraryDownload(bookId: string): Promise<LibraryDownload> {
    if (config.useMock) return mockApi.getLibraryDownload(bookId);
    return request<LibraryDownload>(`/api/library/${encodeURIComponent(bookId)}/download`);
  },

  async postSession(body: PostSessionBody): Promise<PostSessionResponse> {
    if (config.useMock) return mockApi.postSession(body);
    return request<PostSessionResponse>('/api/sessions', { method: 'POST', body: JSON.stringify(body) });
  },

  async getLeaderboard(limit = 20): Promise<LeaderboardResponse> {
    if (config.useMock) return mockApi.getLeaderboard(limit);
    return request<LeaderboardResponse>(`/api/leaderboard${qs({ limit })}`);
  },

  async postReinforcement(): Promise<ReinforcementResponse> {
    if (config.useMock) return mockApi.postReinforcement();
    return request<ReinforcementResponse>('/api/reinforcements', { method: 'POST' });
  },

  async createCheckout(): Promise<CreateCheckoutResponse> {
    if (config.useMock) return mockApi.createCheckout();
    return request<CreateCheckoutResponse>('/api/billing/checkout', { method: 'POST' });
  },

  async getToday(): Promise<TodayPlan> {
    if (config.useMock) return mockApi.getToday();
    return request<TodayPlan>('/api/today');
  },

  async getSchedule(pillarId: string): Promise<ThirtyDaySchedule> {
    if (config.useMock) return mockApi.getSchedule(pillarId);
    return request<ThirtyDaySchedule>(`/api/schedule${qs({ pillarId })}`);
  },

  async getRoutine(): Promise<Routine> {
    if (config.useMock) return mockApi.getRoutine();
    return request<Routine>('/api/routine');
  },

  async updateRoutine(body: UpdateRoutineBody): Promise<Routine> {
    if (config.useMock) return mockApi.updateRoutine(body);
    return request<Routine>('/api/routine', { method: 'PUT', body: JSON.stringify(body) });
  },

  async deleteRoutine(): Promise<Routine> {
    if (config.useMock) return mockApi.deleteRoutine();
    return request<Routine>('/api/routine', { method: 'DELETE' });
  },

  async listJournalEntries(): Promise<JournalEntriesResponse> {
    if (config.useMock) return mockApi.listJournalEntries();
    return request<JournalEntriesResponse>('/api/journal');
  },

  async createJournalEntry(body: CreateJournalEntryBody): Promise<JournalEntry> {
    if (config.useMock) return mockApi.createJournalEntry(body);
    return request<JournalEntry>('/api/journal', { method: 'POST', body: JSON.stringify(body) });
  },

  async updateJournalEntry(entryId: string, body: UpdateJournalEntryBody): Promise<JournalEntry> {
    if (config.useMock) return mockApi.updateJournalEntry(entryId, body);
    return request<JournalEntry>(`/api/journal/${encodeURIComponent(entryId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async deleteJournalEntry(entryId: string): Promise<{ deleted: boolean }> {
    if (config.useMock) return mockApi.deleteJournalEntry(entryId);
    return request<{ deleted: boolean }>(`/api/journal/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
  },

  async exportJournal(): Promise<JournalEntriesResponse> {
    if (config.useMock) return mockApi.exportJournal();
    return request<JournalEntriesResponse>('/api/journal/export');
  },

  async getAdminUsers(q?: string): Promise<AdminUsersResponse> {
    if (config.useMock) return mockApi.getAdminUsers(q);
    return request<AdminUsersResponse>(`/api/admin/users${qs({ q })}`);
  },

  async updateAdminUser(sub: string, body: { tier: Tier }): Promise<AdminUser> {
    if (config.useMock) return mockApi.updateAdminUser(sub, body);
    return request<AdminUser>(`/api/admin/users/${encodeURIComponent(sub)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getAdminAnalytics(): Promise<AdminAnalytics> {
    if (config.useMock) return mockApi.getAdminAnalytics();
    return request<AdminAnalytics>('/api/admin/analytics');
  },
};

export { ApiError };
